import React from "react";
import { useAlert } from "@/hooks/useAlert";
import { useIncidentAcknowledge } from "@/hooks/useIncidentAlerts";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, MapPin, ShieldCheck, Flame, Truck, Users, Radio, CheckCircle2, Phone, Clock, User, Car, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { reverseGeocodeLocation } from "@/lib/geocode";

const SEVERITY_STYLES = {
  critical: "bg-rose-100 text-rose-700 border-rose-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function titleCase(value) {
  return String(value || "")
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatEventTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  const rel = mins < 1 ? "just now" : mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
  return `${d.toLocaleString()} (${rel})`;
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white/60 p-3">
      <Icon size={16} className="mt-0.5 shrink-0 text-slate-500" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <div className="mt-0.5 break-words text-sm font-semibold text-slate-900">{children}</div>
      </div>
    </div>
  );
}

// Define the minimalistic color combinations and icons for each role
const ROLE_THEMES = {
  police: {
    bg: "bg-white/95",
    border: "border-stone-200",
    shadow: "shadow-slate-300/20",
    textPrimary: "text-slate-900",
    textSecondary: "text-slate-600",
    iconBg: "bg-stone-100",
    iconColor: "text-slate-700",
    buttonBg: "bg-slate-800 hover:bg-slate-700",
    Icon: ShieldCheck,
    label: "Police Dispatch",
  },
  fire: {
    bg: "bg-white/95",
    border: "border-stone-200",
    shadow: "shadow-slate-300/20",
    textPrimary: "text-slate-900",
    textSecondary: "text-slate-600",
    iconBg: "bg-stone-100",
    iconColor: "text-slate-700",
    buttonBg: "bg-slate-800 hover:bg-slate-700",
    Icon: Flame,
    label: "Fire Emergency",
  },
  ambulance: {
    bg: "bg-white/95",
    border: "border-stone-200",
    shadow: "shadow-slate-300/20",
    textPrimary: "text-slate-900",
    textSecondary: "text-slate-600",
    iconBg: "bg-stone-100",
    iconColor: "text-slate-700",
    buttonBg: "bg-slate-800 hover:bg-slate-700",
    Icon: Truck,
    label: "Medical Transport",
  },
  hospital: {
    bg: "bg-white/95",
    border: "border-stone-200",
    shadow: "shadow-slate-300/20",
    textPrimary: "text-slate-900",
    textSecondary: "text-slate-600",
    iconBg: "bg-stone-100",
    iconColor: "text-slate-700",
    buttonBg: "bg-slate-800 hover:bg-slate-700",
    Icon: AlertTriangle, // Can use Plus or similar for hospital
    label: "Incoming Patients",
  },
  super_admin: {
    bg: "bg-white/95",
    border: "border-stone-200",
    shadow: "shadow-slate-300/20",
    textPrimary: "text-slate-900",
    textSecondary: "text-slate-600",
    iconBg: "bg-stone-100",
    iconColor: "text-slate-700",
    buttonBg: "bg-slate-800 hover:bg-slate-700",
    Icon: Radio,
    label: "System Alert",
  }
};

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ""));
}

function extractRegistrationNumber(alert) {
  if (!alert || typeof alert !== "object") return "";

  const direct = [
    alert.registration_number,
    alert.registrationNumber,
    alert.vehicle_number,
    alert.vehicleNumber,
    alert.plate,
  ].find((v) => typeof v === "string" && v.trim().length > 0 && !looksLikeUuid(v));
  if (direct) return direct.trim();

  const vehicleObj = alert.vehicle;
  if (vehicleObj && typeof vehicleObj === "object") {
    const fromVehicleObj = [
      vehicleObj.registration_number,
      vehicleObj.registrationNumber,
      vehicleObj.vehicle_number,
      vehicleObj.vehicleNumber,
      vehicleObj.plate,
    ].find((v) => typeof v === "string" && v.trim().length > 0 && !looksLikeUuid(v));
    if (fromVehicleObj) return fromVehicleObj.trim();
  }

  if (Array.isArray(alert.vehicles)) {
    const primary = alert.vehicles.find((v) => v?.role === "PRIMARY") || alert.vehicles[0];
    const fromVehicles = [
      primary?.registration_number,
      primary?.registrationNumber,
      primary?.vehicle_number,
      primary?.vehicleNumber,
      primary?.plate,
    ].find((v) => typeof v === "string" && v.trim().length > 0 && !looksLikeUuid(v));
    if (fromVehicles) return fromVehicles.trim();
  }

  return "";
}

function toDisplayValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value
      .map((entry) => toDisplayValue(entry))
      .filter(Boolean)
      .join(" · ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value).trim();
}

function formatAlertLocation(location) {
  if (!location) return "Location pending...";

  if (typeof location === "string") {
    return location.trim() || "Location pending...";
  }

  if (typeof location === "object") {
    const parts = [location.address, location.city, location.district, location.state].filter(Boolean);
    if (parts.length) return parts.join(", ");
    if (location.lat !== undefined && location.lon !== undefined) {
      return `${location.lat}, ${location.lon}`;
    }
    return toDisplayValue(location) || "Location pending...";
  }

  return toDisplayValue(location) || "Location pending...";
}

function normalizeAlertDetails(alert) {
  if (!alert || typeof alert !== "object") return [];

  const details = [];
  const pushDetail = (label, value) => {
    const cleanLabel = String(label || "").trim();
    const cleanValue = toDisplayValue(value);
    if (!cleanLabel || !cleanValue) return;
    details.push({ label: cleanLabel, value: cleanValue });
  };

  const source = alert.details;
  if (Array.isArray(source)) {
    source.forEach((detail, index) => {
      if (detail && typeof detail === "object") {
        const label = detail?.label || detail?.key || detail?.name || `Detail ${index + 1}`;
        const value = detail?.value ?? detail?.val ?? detail?.data ?? detail?.content ?? "";
        pushDetail(label, value);
        return;
      }
      pushDetail(`Detail ${index + 1}`, detail);
    });
  } else if (source && typeof source === "object") {
    Object.entries(source).forEach(([key, value]) => {
      pushDetail(key, value);
    });
  } else if (source !== undefined && source !== null && String(source).trim() !== "") {
    pushDetail("Details", source);
  }

  // Field-level fallback for backend/system payloads that do not include details[]
  if (details.length === 0) {
    pushDetail("Severity", alert.severity);
    pushDetail("Status", alert.status);
    pushDetail("Responders", alert.responderCount ?? alert.responder_count);
    pushDetail("Vehicles", alert.vehicleCount ?? alert.vehicle_count);
    pushDetail("Description", alert.description);
    pushDetail("Time", alert.timestamp || alert.event_time || alert.created_at);
  }

  return details;
}

export default function EmergencyAlertModal() {
  const { activeAlert, acknowledgeAlert } = useAlert();
  const { acknowledgeIncidentAlert } = useIncidentAcknowledge();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAcknowledging, setIsAcknowledging] = React.useState(false);
  const [acknowledgeError, setAcknowledgeError] = React.useState("");
  const [placeName, setPlaceName] = React.useState("");

  const infoCoords = activeAlert?.info?.coords || null;
  const infoAddress = activeAlert?.info?.addressText || "";
  React.useEffect(() => {
    setPlaceName("");
    if (!infoCoords || infoAddress) return undefined;
    let cancelled = false;
    reverseGeocodeLocation(infoCoords)
      .then((name) => { if (!cancelled && name) setPlaceName(name); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [infoCoords?.lat, infoCoords?.lon, infoAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!activeAlert) return null;

  const info = activeAlert.info || null;

  // Route/theme by org_type (role is only a permission level like org_admin/operator)
  const ORG_TYPE_TO_THEME = {
    PLATFORM: "super_admin",
    POLICE: "police",
    AMBULANCE: "ambulance",
    FIRE_DEPARTMENT: "fire",
  };
  const role = ORG_TYPE_TO_THEME[user?.org_type] || "super_admin";
  const theme = ROLE_THEMES[role] || ROLE_THEMES.super_admin;
  const AlertIcon = theme.Icon;
  const normalizedDetails = normalizeAlertDetails(activeAlert);

  const registrationNumber = extractRegistrationNumber(activeAlert);
  const hasRegistrationDetail = normalizedDetails.some((detail) => {
    const label = detail.label.toLowerCase();
    return label.includes("registration");
  });

  const detailsWithRegistration = (!hasRegistrationDetail && registrationNumber)
    ? [{ label: "Registration Number", value: registrationNumber }, ...normalizedDetails]
    : normalizedDetails;

  const visibleDetails = detailsWithRegistration.filter((detail) => {
    const label = String(detail?.label || "").toLowerCase();
    const value = String(detail?.value || "");
    return !label.includes("incident id") && !label.includes("uuid") && !looksLikeUuid(value);
  });

  const handleAcknowledge = async () => {
    try {
      setIsAcknowledging(true);
      setAcknowledgeError("");
      
      // If this is a backend incident, acknowledge via API
      if (activeAlert.incidentId) {
        await acknowledgeIncidentAlert(activeAlert.incidentId);
        // Defensive: guarantee modal close + siren mute after successful API ack
        acknowledgeAlert();
      } else {
        // Otherwise just close the modal
        acknowledgeAlert();
      }

      // Navigate for ambulance users
      if (role === "ambulance") {
        navigate("/ambulance/active", { state: { selectedId: activeAlert.incidentId || "INC-2041" } });
      }
    } catch (err) {
      console.error("Failed to acknowledge incident:", err);
      setAcknowledgeError("Failed to acknowledge incident. Please try again.");
      toast.error("Failed to acknowledge incident. Please try again.");
    } finally {
      setIsAcknowledging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-all">
      <div 
        className={cn(
          "w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border p-6 shadow-2xl backdrop-blur-md",
          theme.bg, theme.border, theme.shadow
        )}
        style={{ animation: "ciSlideUp 0.3s ease-out forwards" }}
      >
        <div className="flex items-start gap-4">
          <div className={cn("flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl", theme.iconBg)}
               style={{ animation: "ciPulseRing 1.5s ease-out infinite alternate" }}>
            <AlertIcon size={28} className={theme.iconColor} strokeWidth={2} />
          </div>
          <div className="flex-1 pt-1">
            <p className={cn("text-[11px] font-bold uppercase tracking-widest", theme.textSecondary)}>
              {theme.label}
            </p>
            <h2 className={cn("mt-1 text-xl font-bold", theme.textPrimary)}>
              {info ? `${titleCase(info.type)} Alert` : (activeAlert.type || "Critical Incident Detected")}
            </h2>
            {info ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider", SEVERITY_STYLES[info.severity] || SEVERITY_STYLES.high)}>
                  {info.severity}
                </span>
                <span className="rounded-full border border-stone-200 bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                  {titleCase(info.status)}
                </span>
                {info.incidentId ? (
                  <span className="font-mono text-[10px] text-slate-400">#{String(info.incidentId).slice(0, 8).toUpperCase()}</span>
                ) : null}
              </div>
            ) : (
              <p className={cn("mt-1 text-sm font-medium", theme.textSecondary)}>
                {formatAlertLocation(activeAlert.location)}
              </p>
            )}
          </div>
        </div>

        {info ? (
          <div className="mt-5 space-y-2.5">
            <InfoRow icon={Car} label="Vehicle">
              {info.plate ? (
                <>
                  <span className="font-mono tracking-wide">{info.plate}</span>
                  {info.vehicleModel ? <span className="ml-2 font-normal text-slate-500">{info.vehicleModel}</span> : null}
                </>
              ) : <span className="font-normal text-slate-500">Vehicle details unavailable</span>}
            </InfoRow>

            <InfoRow icon={User} label="Driver / Owner">
              {info.driverName ? (
                <>
                  {info.driverName}
                  {info.driverPhone ? (
                    <a href={`tel:${info.driverPhone}`} className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline">
                      <Phone size={12} /> {info.driverPhone}
                    </a>
                  ) : null}
                </>
              ) : <span className="font-normal text-slate-500">Not available</span>}
            </InfoRow>

            <InfoRow icon={MapPin} label="Location">
              <div>{info.addressText || placeName || info.ownerArea || "Locating…"}</div>
              {info.coords ? (
                <a
                  href={`https://www.google.com/maps?q=${info.coords.lat},${info.coords.lon}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-0.5 inline-flex items-center gap-1 font-mono text-[11px] font-normal text-sky-700 hover:underline"
                >
                  {info.coords.lat.toFixed(5)}, {info.coords.lon.toFixed(5)} <ExternalLink size={11} />
                </a>
              ) : null}
            </InfoRow>

            {info.eventTime ? (
              <InfoRow icon={Clock} label="Detected">{formatEventTime(info.eventTime) || info.eventTime}</InfoRow>
            ) : null}

            {info.description ? (
              <InfoRow icon={AlertTriangle} label="Notes">{info.description}</InfoRow>
            ) : null}
          </div>
        ) : (
        <div className="mt-5 grid grid-cols-2 gap-3">
          {visibleDetails.map((detail, idx) => (
             <div key={idx} className={cn("rounded-xl border bg-white/60 p-3", theme.border)}>
               <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                 {detail.label}
               </p>
               <p className={cn("mt-0.5 font-bold", theme.textPrimary)}>
                 {detail.value}
               </p>
             </div>
          ))}
        </div>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={handleAcknowledge}
            disabled={isAcknowledging}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white transition-all",
              isAcknowledging ? "opacity-60 cursor-not-allowed" : theme.buttonBg
            )}
          >
            <CheckCircle2 size={18} /> {isAcknowledging ? "Acknowledging..." : "Acknowledge & Mute"}
          </button>
        </div>

        {acknowledgeError && (
          <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
            {acknowledgeError}
          </p>
        )}
      </div>
    </div>
  );
}
